import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bookmark, Trash2, MapPin } from 'lucide-react';
import { DatePlan } from '../types';
import { StorageService } from '../services/storage';

interface SavedProps {
  onBack: () => void;
}

export default function Saved({ onBack }: SavedProps) {
  const [savedPlans, setSavedPlans] = useState<DatePlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const plans = StorageService.getSavedPlans();
    setSavedPlans(plans);
    setLoading(false);
  }, []);

  const handleDelete = (planId: string) => {
    StorageService.removePlan(planId);
    setSavedPlans(savedPlans.filter(p => p.id !== planId));
  };

  const costPerPerson = (plan: DatePlan) => Math.round(plan.estimatedCost / 2);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block mb-4 p-3 bg-slate-100 rounded-full">
            <Bookmark size={32} className="text-slate-400" />
          </div>
          <p className="text-slate-600">Loading saved dates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 flex items-center gap-3 z-10">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Saved Plans</h1>
          <p className="text-xs text-slate-500">{savedPlans.length} dates saved</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-32">
        {savedPlans.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-96 text-center">
            <div className="text-6xl mb-4">📌</div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No saved plans yet</h2>
            <p className="text-slate-600">
              Save your favorite date plans to keep them for later!
            </p>
          </div>
        ) : (
          <div className="space-y-4 pt-4">
            {savedPlans.map((plan) => (
              <div key={plan.id} className="card">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex gap-2 flex-wrap mb-2">
                      {plan.vibe.map(v => (
                        <span key={v} className="text-xs bg-rose-100 text-rose-800 px-2 py-1 rounded-full">
                          {v}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {plan.title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {plan.story}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Trash2 size={18} className="text-slate-400 hover:text-slate-600" />
                  </button>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-2 mb-4 text-center text-xs">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <div className="font-bold text-slate-900">
                      {Math.round(plan.totalDuration / 60)}h
                    </div>
                    <div className="text-slate-600 text-xs">Time</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <div className="font-bold text-slate-900">
                      ₹{costPerPerson(plan)}
                    </div>
                    <div className="text-slate-600 text-xs">Cost</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <div className="font-bold text-slate-900">
                      {plan.stops.length}
                    </div>
                    <div className="text-slate-600 text-xs">Stops</div>
                  </div>
                  <div className="bg-rose-100 rounded-lg p-2">
                    <div className="font-bold text-rose-900">
                      {plan.noveltyScore}
                    </div>
                    <div className="text-rose-700 text-xs">Novel</div>
                  </div>
                </div>

                {/* Places */}
                <div className="space-y-1 mb-3">
                  {plan.stops.slice(0, 3).map((stop, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-slate-700">
                      <MapPin size={12} className="text-rose-600 flex-shrink-0" />
                      <span>{stop.place.name}</span>
                    </div>
                  ))}
                  {plan.stops.length > 3 && (
                    <div className="text-xs text-slate-500 ml-5">
                      +{plan.stops.length - 3} more
                    </div>
                  )}
                </div>

                {/* Meta */}
                <div className="text-xs text-slate-500">
                  Saved on {new Date(plan.date).toLocaleDateString()}
                </div>

                <button className="mt-3 w-full py-2 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 text-sm font-medium">
                  Start This Date →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
